import { gql } from '@apollo/client';

/**
 * The single canonical shape for a full candidate profile. Every profile query and mutation payload
 * spreads this so the whole aggregate lands in one normalized cache entry (keyed by `id`), keeping the
 * completion meter and every section in sync after any write (docs/development/FrontendGuidelines.md §5.3/§5.5).
 *
 * Uses Apollo's own `gql` (not graphql-codegen's generated tag) — the generated tag is a static
 * string-lookup meant to be consumed via a babel/SWC build-time transform plugin; without that
 * plugin it can neither support `${...}` fragment interpolation nor type-check under plain `tsc`
 * (a tagged-template call always passes a `TemplateStringsArray`, which a generated overload typed
 * `(source: string)` can never structurally accept). Type safety instead comes from the explicit
 * `TypedDocumentNode<TData, TVariables>` annotations on each query/mutation const in this folder,
 * sourced from graphql-codegen's generated `graphql.ts` types (§5.4) — same generated-types benefit,
 * without depending on a new build-tool plugin.
 */
export const TALENT_PROFILE_FIELDS = gql`
  fragment TalentProfileFields on TalentProfile {
    id
    userId
    headline
    summary
    availability
    city
    state
    country
    isOpenToRemote
    profilePhotoUrl
    completionScore
    isVisible
    workExperiences {
      id
      companyName
      title
      employmentType
      startDate
      endDate
      isCurrent
      description
      location
    }
    educations {
      id
      institution
      degree
      fieldOfStudy
      startYear
      endYear
      grade
      isCurrent
    }
    skills {
      id
      name
      proficiency
      yearsOfExperience
      isVerified
      source
    }
    socialLinks {
      id
      platform
      url
    }
    languages {
      id
      name
      proficiency
    }
    projects {
      id
      title
      description
      url
      startDate
      endDate
      isOngoing
    }
    lastWorkingDay
    activeResume {
      id
      fileName
      url
      sizeBytes
      mimeType
      version
      isActive
      parseStatus
      uploadedAt
    }
    createdAt
    updatedAt
  }
`;
